import { Link } from 'react-router-dom';
import { Video, Plus, Play, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useProjects } from '../hooks/useProject';
import Card from '../components/common/Card';
import Button from '../components/common/Button';

export default function Projects() {
  const { projects, isLoading, error } = useProjects();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-jelly-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'composing':
      case 'generating':
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin" />;
      case 'complete':
        return <CheckCircle className="w-4 h-4" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-zinc-400';
      case 'composing':
      case 'generating':
        return 'text-blue-400';
      case 'processing':
        return 'text-yellow-400';
      case 'complete':
        return 'text-green-400';
      case 'failed':
        return 'text-red-400';
      case 'cancelled':
        return 'text-zinc-500';
      default:
        return 'text-zinc-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-zinc-400 mt-1">Your video generation projects</p>
        </div>
        <Link to="/admin/personas">
          <Button variant="secondary">
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        </Link>
      </div>

      {/* Empty state */}
      {projects.length === 0 ? (
        <Card className="text-center py-12">
          <Video className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No projects yet</h3>
          <p className="text-zinc-400 mb-6 max-w-md mx-auto">
            Create a project from one of your personas to start generating AI-powered music videos.
          </p>
          <Link to="/admin/personas">
            <Button size="lg">Go to Personas</Button>
          </Link>
        </Card>
      ) : (
        /* Projects list */
        <div className="space-y-4">
          {projects.map((project) => (
            <Link key={project.id} to={`/admin/projects/${project.id}`}>
              <Card className="hover:border-zinc-700 transition-colors cursor-pointer">
                <div className="flex items-center gap-4">
                  {/* Thumbnail / Preview */}
                  <div className="w-24 h-16 bg-zinc-800 rounded-lg flex items-center justify-center">
                    {project.output_url ? (
                      <video src={project.output_url} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <Video className="w-8 h-8 text-zinc-600" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium capitalize">{project.target_platform} Video</p>
                      <span className={`flex items-center gap-1 text-sm ${getStatusColor(project.status)}`}>
                        {getStatusIcon(project.status)}
                        <span className="capitalize">{project.status}</span>
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400">
                      {project.target_duration}s • {project.quality_tier} quality
                    </p>
                  </div>

                  {/* Cost */}
                  <div className="text-right">
                    {project.actual_cost_usd ? (
                      <p className="font-medium">${project.actual_cost_usd.toFixed(2)}</p>
                    ) : project.estimated_cost_usd ? (
                      <p className="text-zinc-400">~${project.estimated_cost_usd.toFixed(2)}</p>
                    ) : (
                      <p className="text-zinc-500">-</p>
                    )}
                    <p className="text-xs text-zinc-500">{new Date(project.created_at).toLocaleDateString()}</p>
                  </div>

                  {/* Play button for complete */}
                  {project.status === 'complete' && project.output_url && (
                    <div className="ml-4">
                      <div className="w-10 h-10 rounded-full bg-jelly-600 flex items-center justify-center">
                        <Play className="w-5 h-5 ml-0.5" />
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
